import { Score } from "@/components/ui/score";
import { CardEyebrow } from "@/components/ui/card";
import { SaveButton } from "@/components/intel/SaveButton";
import { AddToCollectionMenu } from "@/components/collections/AddToCollectionMenu";

export interface FeedItem {
  id: string;
  title: string;
  url: string;
  source_name: string;
  published_at: number | null;
  geo: string | null;
  summary: string | null;
  why_it_matters: string | null;
  trend_score: number | null;
  opportunity_score: number | null;
  urgency_score: number | null;
  leadership_relevance: number | null;
  relevance_score: number | null;
  category_tags_json: string | null;
  /** Optional — populated by server (`is_saved`) or by client lookup against `useSavedIntelligenceIds`. */
  is_saved?: boolean | number | null;
  /** for_you lens only: which followed category matched, if any. */
  match_category?: string | null;
  /** for_you lens only: the matched geo (either a followed geo or the user's home region). */
  match_geo?: string | null;
}

interface Props {
  item: FeedItem;
  variant?: "full" | "compact";
  emphasis?: "trend" | "opportunity" | "leadership" | null;
  /** Override save state when caller knows it from a cached query (e.g. saved-ids). */
  isSaved?: boolean;
  /** Hide the save toggle entirely (useful on the Saved page where every row is saved). */
  hideSave?: boolean;
  /**
   * Quiet one-line reason this item surfaced, e.g. "Because you follow
   * entrepreneurship". Only passed by the for_you section; other sections
   * render no explanation. Kept subtle on purpose.
   */
  explanation?: string | null;
}

function parseTags(json: string | null): string[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr.slice(0, 2) : [];
  } catch {
    return [];
  }
}

export function ItemCard({
  item,
  variant = "compact",
  emphasis = null,
  isSaved,
  hideSave = false,
  explanation,
}: Props) {
  const tags = parseTags(item.category_tags_json);
  const isFull = variant === "full";
  // Server may send is_saved as 0/1 from SQLite; coerce. Caller `isSaved`
  // (from useSavedIntelligenceIds) wins if provided.
  const savedState =
    isSaved ?? (typeof item.is_saved === "number" ? item.is_saved === 1 : Boolean(item.is_saved));

  return (
    <article className="panel p-5 animate-fade-up group">
      <div className="flex items-start justify-between gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted">
            <span className="text-whisper">{item.source_name}</span>
            {item.geo && <span>· {item.geo}</span>}
            {tags.map((t) => (
              <span key={t}>· {t}</span>
            ))}
          </div>
          <h3 className="mt-2 font-display text-base leading-snug tracking-tight">
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="hover:text-gold-glow transition-colors"
            >
              {item.title}
            </a>
          </h3>
          {item.summary && (
            <p className="mt-2 text-sm text-fog/90 leading-relaxed">{item.summary}</p>
          )}
          {explanation && (
            <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-gold-glow/60">
              {explanation}
            </p>
          )}
          {isFull && item.why_it_matters && (
            <p className="mt-3 text-sm text-whisper leading-relaxed">
              <span className="text-gold-glow">Why it matters — </span>
              {item.why_it_matters}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          {emphasis === "trend" && item.trend_score != null && (
            <Score label="Trend" value={item.trend_score} />
          )}
          {emphasis === "opportunity" && item.opportunity_score != null && (
            <Score label="Opp" value={item.opportunity_score} />
          )}
          {emphasis === "leadership" && item.leadership_relevance != null && (
            <Score label="Lead" value={item.leadership_relevance} />
          )}
          {emphasis === null && isFull && (
            <>
              {item.trend_score != null && <Score label="Trend" value={item.trend_score} />}
              {item.opportunity_score != null && (
                <Score label="Opp" value={item.opportunity_score} />
              )}
              {item.urgency_score != null && (
                <Score label="Urgency" value={item.urgency_score} />
              )}
            </>
          )}
          {!hideSave && (
            <div
              className={`mt-1 flex items-center gap-1 transition-opacity ${
                savedState ? "opacity-100" : "opacity-40 group-hover:opacity-100 focus-within:opacity-100"
              }`}
            >
              <AddToCollectionMenu kind="intelligence" entryId={item.id} />
              <SaveButton itemId={item.id} isSaved={savedState} />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export function ItemCardEmpty({ label, detail }: { label: string; detail?: string }) {
  return (
    <div className="panel p-6">
      <CardEyebrow>Empty</CardEyebrow>
      <p className="mt-2 text-sm text-fog">{label}</p>
      {detail && <p className="mt-1 text-xs text-whisper">{detail}</p>}
    </div>
  );
}
